import { Button, Stack } from "@mui/material";
import { useField, useForm } from "react-final-form";
import { createListCollection } from "@ark-ui/react";

import { LineGraphEdrLine } from "@/lib/api";
import { TextField } from "@/features/shared/components";
import OrderListButton from "@/features/admin/components/order-list-button/OrderListButton";
import { BLUE, GREEN } from "@/features/shared/widgets/graphs/helpers";
import Select from "@/components/select/select";
import { required } from "@/features/shared/components/mui-rff-fields/validation-helpers";
import Fluid from "@/components/fluid/fluid";
import { useTranslation } from "@/lib/i18n";

import { EDRConfiguration } from "./edr/EDRConfiguration";

enum LayerTypes {
  EDR = "edr",
}

interface LineGraphConfigurationProps {
  index: number;
  name: string;
  initialValue: LineGraphEdrLine;
  removeLine: () => void;
  lines: LineGraphEdrLine[];
}

export const LineGraphConfiguration = ({
  index,
  name,
  initialValue,
  removeLine,
  lines,
}: LineGraphConfigurationProps) => {
  const { t } = useTranslation();
  const symbolOptions = [
    {
      label: "Circle",
      value: "circle",
    },
    {
      label: "Square",
      value: "square",
    },
    {
      label: "Diamond",
      value: "diamond",
    },
    {
      label: "Triangle",
      value: "triangle",
    },
    {
      label: "Triangle down",
      value: "triangle-down",
    },
  ];
  const symbolCollection = createListCollection({
    items: symbolOptions,
  });

  const styleCollection = createListCollection({
    items: [
      {
        label: (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            style={{ width: "100%" }}
          >
            <span>Solid</span>
            <span
              style={{
                borderBottom: "2px solid black",
                display: "inlineBlock",
                width: "20px",
              }}
            />
          </Stack>
        ),
        value: "solid",
      },
      {
        label: (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            style={{ width: "100%" }}
          >
            <span>Dashed</span>
            <span
              style={{
                borderBottom: "2px dashed black",
                display: "inlineBlock",
                width: "20px",
              }}
            />
          </Stack>
        ),
        value: "dashed",
      },
    ],
  });

  const linewidthCollection = createListCollection({
    items: [
      {
        label: (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            style={{ width: "100%" }}
          >
            <span>Thin</span>
            <span
              style={{
                borderBottom: "1px solid black",
                display: "inline-block",
                width: "20px",
              }}
            />
          </Stack>
        ),
        value: "thin",
      },
      {
        label: (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            style={{ width: "100%" }}
          >
            <span>Regular</span>
            <span
              style={{
                borderBottom: "2px solid black",
                display: "inline-block",
                width: "20px",
              }}
            />
          </Stack>
        ),
        value: "regular",
      },
      {
        label: (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            style={{ width: "100%" }}
          >
            <span>Thick</span>
            <span
              style={{
                borderBottom: "3px solid black",
                display: "inline-block",
                width: "20px",
              }}
            />
          </Stack>
        ),
        value: "thick",
      },
    ],
  });

  const colorCollection = createListCollection({
    items: [
      {
        label: (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            style={{ width: "100%" }}
          >
            <span>Blue</span>
            <span>
              {BLUE.map((color) => (
                <span
                  key={color}
                  style={{
                    width: "8px",
                    height: "8px",
                    background: color,
                    display: "inline-block",
                  }}
                />
              ))}
            </span>
          </Stack>
        ),
        value: "blue",
      },
      {
        label: (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            style={{ width: "100%" }}
          >
            <span>Green</span>
            <span>
              {GREEN.map((color) => (
                <span
                  key={color}
                  style={{
                    width: "8px",
                    height: "8px",
                    background: color,
                    display: "inline-block",
                  }}
                />
              ))}
            </span>
          </Stack>
        ),
        value: "green",
      },
    ],
  });

  const typeCollection = createListCollection({
    items: [
      ...Object.entries(LayerTypes).map((option) => ({
        label: t(`admin.widgets.fields.lineGraph.lines.${option[1]}`),
        value: option[1],
      })),
    ],
  });

  const form = useForm();
  const typeField = useField<string>(`${name}[${index}].type`, {
    validate: required,
  });
  const styleField = useField(`${name}[${index}].line_style`, {
    defaultValue: "solid",
    validate: required,
  });
  const thicknessField = useField(`${name}[${index}].line_width`, {
    defaultValue: "regular",
  });
  const symbolField = useField(`${name}[${index}].symbol`, {
    defaultValue: symbolOptions[index % symbolOptions.length].value,
    validate: required,
  });
  const colorField = useField(`${name}[${index}].color`, {
    defaultValue: "blue",
  });

  return (
    <Stack border={"1px solid rgba(0,0,0, 0.1)"} padding={2} gap={1}>
      {lines.length > 1 && <OrderListButton name={name} list={lines} index={index} />}
      <TextField
        sx={{ mt: 0 }}
        name={`${name}[${index}].title`}
        label={t("admin.widgets.fields.lineGraph.lines.name")}
        defaultValue={initialValue?.title}
        required
      />

      <Fluid width={180}>
        <Select
          name={styleField.input.name}
          label={t("admin.widgets.fields.lineGraph.lines.lineStyle")}
          collection={styleCollection}
          error={styleField.meta.touched && styleField.meta.error}
          value={styleField.input.value}
          onValueChange={(value) => form.change(styleField.input.name, value)}
        />
        <Select
          name={thicknessField.input.name}
          label={t("admin.widgets.fields.lineGraph.lines.lineWidth")}
          collection={linewidthCollection}
          error={thicknessField.meta.touched && thicknessField.meta.error}
          value={thicknessField.input.value}
          onValueChange={(value) => form.change(thicknessField.input.name, value)}
        />
        <Select
          name={symbolField.input.name}
          label={t("admin.widgets.fields.lineGraph.lines.symbol")}
          collection={symbolCollection}
          error={symbolField.meta.touched && symbolField.meta.error}
          value={symbolField.input.value}
          onValueChange={(value) => form.change(symbolField.input.name, value)}
        />
        <Select
          name={colorField.input.name}
          label={t("admin.widgets.fields.lineGraph.lines.color")}
          collection={colorCollection}
          error={colorField.meta.touched && colorField.meta.error}
          value={colorField.input.value}
          onValueChange={(value) => form.change(colorField.input.name, value)}
        />
      </Fluid>

      <Select
        name={typeField.input.name}
        label={t("admin.widgets.fields.lineGraph.lines.type")}
        collection={typeCollection}
        error={typeField.meta.touched && typeField.meta.error}
        value={typeField.input.value}
        onValueChange={(value) => form.change(typeField.input.name, value)}
      />

      {typeField.input.value === LayerTypes.EDR && (
        <EDRConfiguration
          name={`${name}[${index}]`}
          initialValue={initialValue}
          type={typeField.input.value}
        />
      )}

      <Button onClick={removeLine}>
        {t("admin.widgets.fields.lineGraph.lines.remove")}
      </Button>
    </Stack>
  );
};
